// Atmospheric layer — README.md §7 + §7.9.1 (monopo saigon "shifting gradient depths
// on frosted glass") + DESIGN.md §8.
//
// All four sub-layers are pure CSS (no JS / no GSAP / no PixiJS) so they composite
// on the GPU and don't tie up the main thread. Particles (PixiJS) are intentionally
// out of scope here — they're an opt-in lazy module added later (§14 polish).
//
// Z-stack inside this component (lowest → highest):
//   0  Drift gradient   — radial blends slowly shifting position
//   1  Frosted spheres  — three independent organic blobs, mix-blend-mode: screen
//   2  Light shafts     — diagonal cyan beams drifting top → bottom
//   3  Noise grain      — static SVG feTurbulence overlay
//
// Content must sit above this stack — wrap section content in a `relative z-10`
// container. The layer itself is `pointer-events: none` + `aria-hidden`.
//
// Per-screen variations come from §7.8 (Hero = full, Pain = calm, etc.); for now
// expose `intensity`, `spheres`, `shafts` so individual modules can tune without
// editing CSS.
//
// Wave 4 (2026-06-04, master prompt §2.5): when this component is rendered inside
// the seamless `<Showcase>` flow, the global `AtmosphericLayer` already paints the
// backdrop once at app root — per-module instances would double-paint. Through
// `SeamlessFlowContext` we suppress per-module renders inside that flow. In
// `/preview/:moduleId` the context stays `false` and modules render their own
// atmosphere as before (isolated iPhone preview frame).

import { useSeamlessFlow } from '../../app/SeamlessFlowContext';

interface AtmosphericLayerProps {
  /** Number of frosted spheres to render (max 3). Default 3. */
  spheres?: 0 | 1 | 2 | 3;
  /** Number of light shafts to render (max 3). Default 2. */
  shafts?: 0 | 1 | 2 | 3;
  /** Intensity preset for the drift gradient. README.md §7.8. */
  intensity?: 'full' | 'calm' | 'reduced';
  /** Render the static SVG noise overlay. Default true. */
  noise?: boolean;
}

export function AtmosphericLayer({
  spheres = 3,
  shafts = 2,
  intensity = 'full',
  noise = true,
}: AtmosphericLayerProps = {}) {
  // In seamless flow the global atmosphere at app root covers all modules.
  // Per-module instances would double-paint, costing perf and breaking continuity.
  const suppress = useSeamlessFlow();
  if (suppress) return null;

  return (
    <div className="atm-layer" aria-hidden>
      <div className={`atm-drift atm-drift--${intensity}`} />

      {spheres >= 1 && <div className="atm-sphere atm-sphere--a" />}
      {spheres >= 2 && <div className="atm-sphere atm-sphere--b" />}
      {spheres >= 3 && <div className="atm-sphere atm-sphere--c" />}

      {shafts >= 1 && <div className="atm-shaft atm-shaft--a" />}
      {shafts >= 2 && <div className="atm-shaft atm-shaft--b" />}
      {shafts >= 3 && <div className="atm-shaft atm-shaft--c" />}

      {noise && (
        <svg
          className="atm-noise"
          aria-hidden
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <filter id="atm-noise-filter">
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves={2} stitchTiles="stitch" />
          </filter>
          <rect width="100%" height="100%" filter="url(#atm-noise-filter)" />
        </svg>
      )}
    </div>
  );
}
